import React, { Component, type ErrorInfo } from 'react';
import './ErrorBoundary.css';

interface Props {
  children: React.ReactNode;
  /** Short label used in the fallback copy and telemetry, e.g.
   *  "Messages" or "Admin dashboard". Defaults to "app". */
  scope?: string;
  /** Optional custom fallback. When omitted the boundary renders its
   *  default full-screen "Something went wrong" screen for app-root
   *  wrappers, or the compact inline fallback when `scope` is set. */
  fallback?: (err: Error, reset: () => void) => React.ReactNode;
  /** Called with the error + component stack. Forwarded in addition
   *  to the default console.error + Sentry capture. */
  onError?: (err: Error, info: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Forward to Sentry if wired up (no direct import needed — Sentry's
    // global error handler captures unhandled errors automatically, but
    // error boundaries require an explicit call).
    const sentry = (window as unknown as Record<string, unknown>).__SENTRY__;
    if (sentry && typeof (sentry as Record<string, unknown>).captureException === 'function') {
      ((sentry as Record<string, unknown>).captureException as (e: unknown, ctx: unknown) => void)(
        error,
        { extra: { componentStack: info.componentStack, scope: this.props.scope } },
      );
    }
    // Always log to console so devtools show the full stack.
    console.error(`[ErrorBoundary${this.props.scope ? `:${this.props.scope}` : ''}]`, error, info.componentStack);
    this.props.onError?.(error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // 1) Custom fallback wins.
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }
      // 2) Scoped inline fallback for module-level boundaries — keeps
      //    the rest of the shell (sidebar, topbar) usable.
      if (this.props.scope) {
        return (
          <div className="error-boundary error-boundary--scoped" role="alert">
            <h2 className="error-boundary__title">
              {this.props.scope} hit a problem
            </h2>
            <p className="error-boundary__body">
              This section crashed. The rest of the app is still usable —
              you can retry this view or return home.
            </p>
            <code className="error-boundary__detail">
              {this.state.error.message}
            </code>
            <div className="error-boundary__actions">
              <button
                type="button"
                className="error-boundary__btn error-boundary__btn--primary"
                onClick={this.handleReset}
              >
                Retry this section
              </button>
              <button
                type="button"
                className="error-boundary__btn"
                onClick={this.handleReload}
              >
                Reload page
              </button>
            </div>
          </div>
        );
      }
      // 3) App-root full-screen fallback.
      return (
        <div className="error-boundary error-boundary--fullscreen">
          <h1 className="error-boundary__title">Something went wrong</h1>
          <p className="error-boundary__body">
            An unexpected error occurred. Reloading the page usually fixes it.
          </p>
          <code className="error-boundary__detail">
            {this.state.error.message}
          </code>
          <button
            type="button"
            className="error-boundary__btn error-boundary__btn--primary"
            onClick={this.handleReload}
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
